import { Injectable } from '@angular/core';
import {
  initialize,
  LDClient,
  LDUser
} from 'launchdarkly-js-client-sdk';

import { environment } from '../environments/environment';

/**
 * Thin wrapper around the LaunchDarkly browser SDK.
 *
 * The client is initialized lazily on the first `isEnabled` call. If the
 * configured client-side ID is missing (e.g. in environments where the value
 * has not yet been provisioned) or if SDK initialization throws, every flag
 * evaluation resolves to its supplied default value (fail-closed).
 *
 * Anonymous contexts are used because the HRA Angular app does not currently
 * pass per-user targeting attributes for feature flags.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private clientPromise: Promise<LDClient | null> | null = null;

  async isEnabled(flagKey: string, defaultValue: boolean = false): Promise<boolean> {
    try {
      const client = await this.getClient();
      if (!client) {
        return defaultValue;
      }
      const variation = client.variation(flagKey, defaultValue);
      return variation === true;
    } catch (err) {
      console.warn(`[FeatureFlagService] isEnabled('${flagKey}') failed, returning default`, err);
      return defaultValue;
    }
  }

  private getClient(): Promise<LDClient | null> {
    if (this.clientPromise) {
      return this.clientPromise;
    }

    const clientId = environment.launchDarklyClientId;
    if (!clientId) {
      this.clientPromise = Promise.resolve(null);
      return this.clientPromise;
    }

    this.clientPromise = new Promise<LDClient | null>(resolve => {
      try {
        const context: LDUser = { key: 'anonymous-hra', anonymous: true };
        const client = this.createClient(clientId, context);
        client.waitForInitialization()
          .then(() => resolve(client))
          .catch(err => {
            console.warn('[FeatureFlagService] LaunchDarkly initialization failed', err);
            resolve(null);
          });
      } catch (err) {
        console.warn('[FeatureFlagService] LaunchDarkly initialize() threw', err);
        resolve(null);
      }
    });

    return this.clientPromise;
  }

  /** Overridable in tests — `initialize` is non-writable on the LD module. */
  protected createClient(clientId: string, context: LDUser): LDClient {
    return initialize(clientId, context);
  }
}
